import { Button, Segmented } from "@community-project-org/ui";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

/** Ein Platz, wie `/offers/{id}/seats` ihn liefert. */
export interface SeatCell {
  row: string;
  number: number;
  premium: boolean;
  taken: boolean;
}

/** Stabiler Schlüssel eines Platzes, etwa „F8“. */
export function seatKey(seat: { row: string; number: number }): string {
  return `${seat.row}${seat.number}`;
}

/** Ordnet die Plätze nach Reihe, in der Reihenfolge, in der der Server sie liefert. */
export function rowsOf(seats: SeatCell[]): { label: string; seats: SeatCell[] }[] {
  const rows = new Map<string, SeatCell[]>();
  for (const seat of seats) {
    const row = rows.get(seat.row) ?? [];
    row.push(seat);
    rows.set(seat.row, row);
  }
  return [...rows.entries()].map(([label, entries]) => ({
    label,
    seats: entries.sort((a, b) => a.number - b.number),
  }));
}

const ZOOM = [1, 1.5, 2] as const;

interface SeatMapProps {
  seats: SeatCell[];
  /** Gewählte Plätze als Schlüssel (`seatKey`). */
  selected: ReadonlySet<string>;
  onToggle: (seat: SeatCell) => void;
  /** Höchstzahl in einer Buchung; darüber bleibt ein freier Platz unantastbar. */
  max: number;
}

/**
 * Sitzplan mit Zoom und Listenalternative.
 *
 * Sitze sind bewusst kleiner als 44 px (StyleGuide: Sitzplan) — ein Saal mit 14
 * Plätzen je Reihe passte sonst nicht auf ein Telefon. Dafür gibt es Zoom und die
 * Liste, in der jeder Platz ein volles Touch-Ziel ist. Belegt ist für die Tastatur
 * deaktiviert, nicht nur grau.
 */
export function SeatMap({ seats, selected, onToggle, max }: SeatMapProps): JSX.Element {
  const [view, setView] = useState<"map" | "list">("map");
  const [zoom, setZoom] = useState<(typeof ZOOM)[number]>(1);
  const rows = rowsOf(seats);
  const full = selected.size >= max;

  const label = (seat: SeatCell): string =>
    `Reihe ${seat.row}, Platz ${seat.number}${seat.premium ? ", Premium" : ""}${
      seat.taken ? ", belegt" : ""
    }`;

  return (
    <div className="grid gap-3">
      <Segmented
        label="Ansicht"
        value={view}
        onChange={setView}
        options={[
          { value: "map", label: "Saalplan" },
          { value: "list", label: "Liste" },
        ]}
      />

      {view === "map" ? (
        <>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              aria-label="Verkleinern"
              disabled={zoom === ZOOM[0]}
              onClick={() => setZoom(ZOOM[Math.max(0, ZOOM.indexOf(zoom) - 1)] ?? 1)}
            >
              <Minus size={16} aria-hidden="true" />
            </Button>
            <span className="num w-12 text-center text-[13px] font-bold text-ink-2">
              {Math.round(zoom * 100)} %
            </span>
            <Button
              variant="secondary"
              aria-label="Vergrössern"
              disabled={zoom === ZOOM[ZOOM.length - 1]}
              onClick={() =>
                setZoom(ZOOM[Math.min(ZOOM.length - 1, ZOOM.indexOf(zoom) + 1)] ?? 2)
              }
            >
              <Plus size={16} aria-hidden="true" />
            </Button>
          </div>

          {/* Gezoomt scrollt nur der Saal, nie die Seite. `zoom` statt `transform`,
              damit der vergrösserte Saal auch Platz einnimmt und scrollbar wird. */}
          <div className="overflow-auto rounded border border-line bg-paper py-3">
            <p className="micro pb-3 text-center">Leinwand / Bühne</p>
            <div className="seatmap" style={{ zoom, minWidth: "max-content" }}>
              {rows.map((row) => (
                <div key={row.label} className="seatrow">
                  <span className="rl" aria-hidden="true">
                    {row.label}
                  </span>
                  {row.seats.map((seat) => {
                    const held = selected.has(seatKey(seat));
                    return (
                      <button
                        key={seatKey(seat)}
                        type="button"
                        className={`seat${held ? " held" : ""}${seat.taken ? " booked" : ""}`}
                        data-cat={seat.premium ? "premium" : undefined}
                        aria-label={label(seat)}
                        aria-pressed={held}
                        disabled={seat.taken || (!held && full)}
                        onClick={() => onToggle(seat)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="grid max-h-[360px] gap-3 overflow-y-auto">
          {rows.map((row) => {
            const free = row.seats.filter((seat) => !seat.taken);
            return (
              <fieldset key={row.label} className="grid gap-1.5">
                <legend className="text-[12px] font-bold text-ink-2">
                  Reihe {row.label} · <span className="num">{free.length}</span> frei
                </legend>
                <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                  {free.map((seat) => {
                    const held = selected.has(seatKey(seat));
                    return (
                      <Button
                        key={seatKey(seat)}
                        variant={held ? "primary" : "secondary"}
                        aria-pressed={held}
                        aria-label={label(seat)}
                        disabled={!held && full}
                        onClick={() => onToggle(seat)}
                      >
                        <span className="num">{seatKey(seat)}</span>
                      </Button>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>
      )}

      <div className="seatlegend">
        <span>
          <i />
          frei
        </span>
        <span>
          <i className="prem" />
          Premium
        </span>
        <span>
          <i className="held" />
          deine Auswahl
        </span>
        <span>
          <i className="booked" />
          belegt
        </span>
      </div>
    </div>
  );
}
